import { IStringTool } from "../core/IStringTool"

/**
 * Input parameters for EasingFunctionTool.
 */
interface EasingFunctionInput {
  /**
   * The easing string.
   * Can be: 'linear', 'ease', 'ease-in', 'ease-out', 'ease-in-out', or 'cubic-bezier(...)'
   */
  easing: string
}

/**
 * Output of the easing function: receives t in [0,1] and returns eased value.
 */
export type EasingFunctionOutput = (t: number) => number

/**
 * Tool for parsing easing strings into easing functions.
 * Supports standard keywords (`ease-in`, `ease-out`, etc.) and `cubic-bezier(...)` expressions.
 */
export default class EasingFunctionTool implements IStringTool<EasingFunctionInput, EasingFunctionOutput> {
  private namedCurves: Record<string, [number, number, number, number]> = {
    "linear": [0, 0, 1, 1],
    "ease": [0.25, 0.1, 0.25, 1],
    "ease-in": [0.42, 0, 1, 1],
    "ease-out": [0, 0, 0.58, 1],
    "ease-in-out": [0.42, 0, 0.58, 1],
  }

  /**
   * Parses an easing string and returns a corresponding easing function.
   */
  process({ easing }: EasingFunctionInput): EasingFunctionOutput {
    const def = easing.trim()

    if (this.namedCurves[def]) {
      return this.cubicBezier(...this.namedCurves[def])
    }

    const match = def.match(/^cubic-bezier\s*\(\s*([-+]?\d*\.?\d+)\s*,\s*([-+]?\d*\.?\d+)\s*,\s*([-+]?\d*\.?\d+)\s*,\s*([-+]?\d*\.?\d+)\s*\)$/);

    if (match) {
      const [x1, y1, x2, y2] = match.slice(1).map(Number)
      return this.cubicBezier(x1, y1, x2, y2)
    }

    // fallback: linear
    return (t: number) => t
  }

  /**
   * Generates a cubic-bezier easing function.
   * Ported from https://github.com/gre/bezier-easing (MIT)
   */
  private cubicBezier(x1: number, y1: number, x2: number, y2: number): EasingFunctionOutput {
    const cx = 3 * x1
    const bx = 3 * (x2 - x1) - cx
    const ax = 1 - cx - bx

    const cy = 3 * y1
    const by = 3 * (y2 - y1) - cy
    const ay = 1 - cy - by

    function sampleCurveX(t: number) {
      return ((ax * t + bx) * t + cx) * t
    }

    function sampleCurveY(t: number) {
      return ((ay * t + by) * t + cy) * t
    }

    function sampleCurveDerivativeX(t: number) {
      return (3 * ax * t + 2 * bx) * t + cx
    }

    function solveCurveX(x: number, epsilon = 1e-5) {
      let t0, t1, t2 = x, x2, d2, i

      // Newton-Raphson method
      for (i = 0; i < 8; i++) {
        x2 = sampleCurveX(t2) - x
        if (Math.abs(x2) < epsilon) return t2
        d2 = sampleCurveDerivativeX(t2)
        if (Math.abs(d2) < 1e-6) break
        t2 = t2 - x2 / d2
      }

      // Bisection fallback
      t0 = 0
      t1 = 1
      t2 = x

      while (t0 < t1) {
        x2 = sampleCurveX(t2) - x
        if (Math.abs(x2) < epsilon) return t2
        if (x2 > 0) t1 = t2
        else t0 = t2
        t2 = (t1 + t0) / 2
      }

      return t2
    }

    return function (x: number) {
      return sampleCurveY(solveCurveX(x))
    }
  }
}
